/**
 * The punch screens' formatting, and only the parts the shared formatters do
 * not already own.
 *
 * Two rules from `DESIGN.md` shape everything here:
 *
 * - **Two date forms.** A grid or a readout gets the ISO/24-hour form; prose
 *   gets the long French one. The *separator* between hours and minutes is a
 *   translation, not a format — « 22 h 00 » in French, `22:00` in English — so
 *   these functions return the two halves and the component assembles them
 *   through `t('punch:format.clock')`. That is why nothing here returns a
 *   ready-made "22 h 00".
 * - **Site timezone, always.** Every instant on a worker screen renders in the
 *   site's zone (`01-PRODUCT-SPEC` §Timezone display). Luxon's
 *   `DateTime.fromISO(iso, { zone })` *converts* into the zone; the
 *   `setZone: true` variant keeps the offset written in the string, which for
 *   our `Z`-suffixed instants means "stay in UTC" while looking entirely
 *   plausible.
 */
import { durationParts, intlLocale, type DurationParts } from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import { DateTime } from 'luxon';

export { crossesMidnight, zoneAbbreviation } from '@saniplus/api-client';
export type { DurationParts };

/**
 * The zone every instant on a worker screen renders in, in one place.
 *
 * Three screens wrote this chain by hand and two of them disagreed about the
 * fallback — one answered `'America/Montreal'`, one answered `null`. Both
 * answers are kept, because which one is right depends on the caller: a screen
 * that must draw a time takes `siteTimezoneOf` and its fallback, while the
 * shell's clock takes `siteTimezoneOrNull` and draws nothing rather than
 * asserting a Montréal time for a plant it cannot name.
 *
 * The open session wins because it is the shift the worker is measurably
 * inside; the scheduled shift and then the next one follow.
 */
const FALLBACK_TIME_ZONE = 'America/Montreal';

type SiteZoneContext =
  | {
      readonly openSession?: { readonly site: { readonly timezone: string } } | null;
      readonly currentShift?: { readonly site: { readonly timezone: string } } | null;
      readonly upcomingShifts?: readonly { readonly site: { readonly timezone: string } }[];
    }
  | null
  | undefined;

/**
 * The site zone, or `null` when the context names no site at all.
 *
 * Separate from `siteTimezoneOf` because the two callers want different things
 * from the same chain. A screen that must draw a time needs *a* zone and takes
 * the fallback; the shell's clock would rather draw nothing than assert a
 * Montréal time for a worker whose plant it cannot name — a plausible-but-wrong
 * value is the failure mode the hardcoded-zone sweep existed to remove.
 */
export function siteTimezoneOrNull(context: SiteZoneContext): string | null {
  return (
    context?.openSession?.site.timezone ??
    context?.currentShift?.site.timezone ??
    context?.upcomingShifts?.[0]?.site.timezone ??
    null
  );
}

export function siteTimezoneOf(context: SiteZoneContext): string {
  return siteTimezoneOrNull(context) ?? FALLBACK_TIME_ZONE;
}

/**
 * The two halves of a clock time, zero-padded. A duration's halves come from
 * `elapsedParts` below, which is the shared formatter's shape.
 *
 * A **type alias**, not an interface, on purpose: only an alias gets TypeScript's
 * implicit index signature, and without one it cannot be handed to `t()` as
 * interpolation values.
 */
type ClockParts = {
  readonly hh: string;
  readonly mm: string;
};

function zoned(iso: string, zone: string, locale: Locale): DateTime {
  return DateTime.fromISO(iso, { zone }).setLocale(intlLocale(locale));
}

function pad(value: number): string {
  return String(Math.trunc(value)).padStart(2, '0');
}

/** `22 h 00` as `{ hh: '22', mm: '00' }`, in the site's zone. `null` if unparseable. */
export function clockParts(iso: string, zone: string): ClockParts | null {
  const dt = DateTime.fromISO(iso, { zone });
  if (!dt.isValid) return null;
  return { hh: pad(dt.hour), mm: pad(dt.minute) };
}

/**
 * A live elapsed span as duration parts, for a `{{hours}} h {{minutes}}` key.
 *
 * The only thing this adds to the shared `durationParts` is the unit: a timer
 * counts milliseconds and the formatter counts minutes. Truncation, the
 * two-digit minutes and the null rule are all the shared one's — and `null`
 * cannot come back out of a finite span, so the timers never branch on it.
 */
export function elapsedParts(ms: number): DurationParts {
  return durationParts(Math.max(0, ms) / 60_000) ?? { hours: '0', minutes: '00' };
}

/** Whole minutes in a span, truncated toward zero. */
export function minutesOf(ms: number): number {
  return Math.max(0, Math.floor(Math.abs(ms) / 60_000));
}

/** Whole seconds in a span — the reconciliation drift. */
export function secondsOf(ms: number): number {
  return Math.max(0, Math.round(Math.abs(ms) / 1_000));
}

/** `jeudi 27 août` — the prose date form, without the year. */
export function longDate(iso: string, zone: string, locale: Locale): string {
  const dt = zoned(iso, zone, locale);
  if (!dt.isValid) return '';
  return dt.toLocaleString({ weekday: 'long', day: 'numeric', month: 'long' });
}

/** `27 août` — the same form with no weekday, for a tight fact row. */
export function shortDate(iso: string, zone: string, locale: Locale): string {
  const dt = zoned(iso, zone, locale);
  if (!dt.isValid) return '';
  return dt.toLocaleString({ day: 'numeric', month: 'long' });
}

/** `vendredi` — the right-hand cap on the "next shift" readout. */
export function weekday(iso: string, zone: string, locale: Locale): string {
  const dt = zoned(iso, zone, locale);
  return dt.isValid ? dt.toLocaleString({ weekday: 'long' }) : '';
}

/** `18` — the day number the blocked state renders at readout scale. */
export function dayNumber(iso: string, zone: string): string {
  const dt = DateTime.fromISO(iso, { zone });
  return dt.isValid ? String(dt.day) : '';
}

/** `août` — the unit beside that day number. */
export function monthName(iso: string, zone: string, locale: Locale): string {
  const dt = zoned(iso, zone, locale);
  return dt.isValid ? dt.toLocaleString({ month: 'long' }) : '';
}

/** The city a site is in, for the "heure du site" footnote. Falls back to its name. */
export function siteCity(site: {
  readonly name: string;
  readonly city?: string | null | undefined;
}): string {
  const city = site.city;
  return city === undefined || city === null || city === '' ? site.name : city;
}

/** Where a day sits relative to the one the worker is standing in. */
type RelativeDay = 'today' | 'tomorrow' | 'yesterday' | 'other';

/**
 * Today, tomorrow, yesterday — or nothing, in the SITE's zone.
 *
 * « Vendredi 28 août » is precise and answers the wrong question first: a
 * worker looking at the next shift wants to know *how soon*, and then which
 * day. The two together — « Demain, vendredi 28 août » — answer both without
 * making anyone count, and keeping the absolute date is what stops the
 * relative word being a payroll claim on its own. A word like *demain* read at
 * 23 h 58 is true for two more minutes; the date beside it is true all night.
 *
 * Compared on CALENDAR DAYS in the site's zone, not on elapsed hours: a shift
 * starting in twenty-five hours can still be tomorrow, and one starting in
 * three can already be the day after. Both instants are floored to their local
 * start-of-day first, so the answer changes at local midnight — which is the
 * moment the words themselves change meaning.
 */
export function relativeDay(iso: string, zone: string, now: number): RelativeDay {
  const at = DateTime.fromISO(iso, { zone });
  const today = DateTime.fromMillis(now, { zone });
  if (!at.isValid || !today.isValid) return 'other';
  const days = at.startOf('day').diff(today.startOf('day'), 'days').days;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return 'other';
}

/** Milliseconds from `now` to an instant. Negative when the instant has passed. */
export function msUntil(iso: string, now: number): number | null {
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : at - now;
}

/**
 * Where an instant sits inside a window, as a percentage clamped to 0–100.
 *
 * The punch-window rail draws the attempt against the 60/60 window; an attempt
 * outside it pins to an edge rather than escaping the track.
 */
export function positionInWindow(instantIso: string, fromIso: string, toIso: string): number | null {
  const at = Date.parse(instantIso);
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(at) || Number.isNaN(from) || Number.isNaN(to) || to <= from) return null;
  return Math.min(100, Math.max(0, ((at - from) / (to - from)) * 100));
}
